import { ReactNode, useMemo } from 'react';
import {
  parseEntityRef,
  stringifyEntityRef,
  type Entity,
} from '@backstage/catalog-model';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  EntityUserFilter,
  useEntityList,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, Theme, useTheme } from '@mui/material/styles';
import useAsync from 'react-use/esm/useAsync';
import {
  getSurfaceBorder,
  getSurfaceColor,
  getThemeTokens,
  radii,
} from '../../theme/tokens';

export const explorerFilterColumnWidth = 260;

const lifecycleStyles = {
  experimental: {
    backgroundColor: '#E8E9FF',
    color: '#4A56D4',
  },
  internal: {
    backgroundColor: '#EEF1F5',
    color: '#5E6A7A',
  },
  production: {
    backgroundColor: '#E5F8E8',
    color: '#0D8D58',
  },
} as const;

const buildEntityQueryFilter = (
  filters: Record<string, unknown>,
  excludedKeys: string[] = [],
) => {
  const queryFilter: Record<string, string | string[]> = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (!value || excludedKeys.includes(key)) {
      return;
    }

    const catalogFilters = (
      value as { getCatalogFilters?: () => Record<string, string | string[]> }
    ).getCatalogFilters?.();

    if (!catalogFilters) {
      return;
    }

    Object.assign(queryFilter, catalogFilters);
  });

  return queryFilter;
};

const useOwnedCount = () => {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const { filters } = useEntityList();

  const { value: ownershipEntityRefs = [] } = useAsync(async () => {
    const identity = await identityApi.getBackstageIdentity();
    return identity.ownershipEntityRefs;
  }, [identityApi]);

  const baseFilter = useMemo(
    () => buildEntityQueryFilter(filters, ['user']),
    [filters],
  );

  const selectedOwnerRefs = (
    filters.owners as { values?: string[] } | undefined
  )?.values;
  const effectiveOwnerRefs = selectedOwnerRefs?.length
    ? selectedOwnerRefs.filter(ref => ownershipEntityRefs.includes(ref))
    : ownershipEntityRefs;

  const { value: count = 0 } = useAsync(async () => {
    if (!effectiveOwnerRefs.length) {
      return 0;
    }

    const response = await catalogApi.queryEntities({
      filter: {
        ...baseFilter,
        'relations.ownedBy': effectiveOwnerRefs,
      },
      limit: 0,
    });

    return response.totalItems ?? 0;
  }, [catalogApi, JSON.stringify(baseFilter), effectiveOwnerRefs.join('|')]);

  return {
    count,
    filter: EntityUserFilter.owned(ownershipEntityRefs),
  };
};

const useStarredCount = () => {
  const catalogApi = useApi(catalogApiRef);
  const { filters } = useEntityList();
  const { starredEntities } = useStarredEntities();

  const baseFilter = useMemo(
    () => buildEntityQueryFilter(filters, ['user']),
    [filters],
  );

  const starredRefs = Array.from(starredEntities);
  const starredNames = starredRefs.map(ref => parseEntityRef(ref).name);

  const { value: count = 0 } = useAsync(async () => {
    if (!starredNames.length) {
      return 0;
    }

    const response = await catalogApi.queryEntities({
      filter: {
        ...baseFilter,
        'metadata.name': starredNames,
      },
      limit: 1000,
    });

    return response.items.filter(entity =>
      starredEntities.has(
        stringifyEntityRef({
          kind: entity.kind,
          namespace: entity.metadata.namespace,
          name: entity.metadata.name,
        }),
      ),
    ).length;
  }, [catalogApi, JSON.stringify(baseFilter), starredRefs.join('|')]);

  return {
    count,
    filter: EntityUserFilter.starred(starredRefs),
  };
};

const ExplorerQuickFilterRow = ({
  checked,
  count,
  label,
  onClick,
}: {
  checked: boolean;
  count: number;
  label: string;
  onClick: () => void;
}) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'flex-start',
      minHeight: 34,
      width: '100%',
    }}
  >
    <Stack alignItems="center" direction="row" spacing={1}>
      <Checkbox checked={checked} size="small" sx={{ p: 0 }} />
      <Typography sx={{ fontSize: 14 }}>
        {label} ({count})
      </Typography>
    </Stack>
  </ButtonBase>
);

export const ExplorerUserFilters = ({
  sectionLabel = 'Meltwater',
}: {
  sectionLabel?: string;
}) => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  const { filters, totalItems, updateFilters } = useEntityList();
  const owned = useOwnedCount();
  const starred = useStarredCount();
  const selected =
    (filters.user as { value?: string } | undefined)?.value ?? 'all';

  const setUserFilter = (value: 'all' | 'owned' | 'starred') => {
    updateFilters({
      user:
        value === 'owned'
          ? owned.filter
          : value === 'starred'
            ? starred.filter
            : EntityUserFilter.all(),
    });
  };

  return (
    <Stack spacing={2.25}>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.25 }}>
          Quick filters
        </Typography>
        <Stack spacing={0.875}>
          <ExplorerQuickFilterRow
            checked={selected === 'owned'}
            count={owned.count}
            label="Owned"
            onClick={() =>
              setUserFilter(selected === 'owned' ? 'all' : 'owned')
            }
          />
          <ExplorerQuickFilterRow
            checked={selected === 'starred'}
            count={starred.count}
            label="Starred"
            onClick={() =>
              setUserFilter(selected === 'starred' ? 'all' : 'starred')
            }
          />
        </Stack>
      </Box>

      <Divider sx={{ borderColor: getSurfaceBorder(theme) }} />

      <Box>
        <Typography
          sx={{
            color: tokens.textPrimary,
            fontSize: 14,
            fontWeight: 700,
            mb: 1.25,
          }}
        >
          {sectionLabel}
        </Typography>
        <ButtonBase
          onClick={() => setUserFilter('all')}
          sx={{
            alignItems: 'center',
            backgroundColor: getSurfaceColor(theme, 'raised'),
            border: `1px solid ${getSurfaceBorder(theme)}`,
            borderRadius: `${radii.md}px`,
            display: 'flex',
            justifyContent: 'flex-start',
            minHeight: 44,
            overflow: 'hidden',
            position: 'relative',
            px: 1.5,
            width: '100%',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              borderRadius: 999,
              height: 'calc(100% - 14px)',
              left: 8,
              opacity: selected === 'all' ? 1 : 0,
              position: 'absolute',
              top: 7,
              width: 4,
            }}
          />
          <Typography sx={{ fontSize: 14, pl: 1.5 }}>
            All ({totalItems ?? 0})
          </Typography>
        </ButtonBase>
      </Box>
    </Stack>
  );
};

export const ExplorerAutocompleteField = ({
  children,
  hasValue,
  placeholder,
}: {
  children: ReactNode;
  hasValue: boolean;
  placeholder: string;
}) => (
  <Box
    data-catalog-autocomplete-field
    data-has-value={hasValue ? 'true' : 'false'}
    data-placeholder={placeholder}
  >
    {children}
  </Box>
);

export const getEntityLifecycle = (entity: Entity) => {
  const lifecycle = entity.spec?.lifecycle;
  return typeof lifecycle === 'string' ? lifecycle : undefined;
};

export const renderLifecycleBadge = (lifecycle?: string) => {
  if (!lifecycle) {
    return '—';
  }

  const style =
    lifecycleStyles[lifecycle as keyof typeof lifecycleStyles] ??
    lifecycleStyles.internal;

  return (
    <Box
      component="span"
      sx={{
        ...style,
        borderRadius: 999,
        display: 'inline-flex',
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1,
        px: 1.1,
        py: 0.55,
        textTransform: 'lowercase',
      }}
    >
      {lifecycle}
    </Box>
  );
};

export const renderTags = (tags?: string[]) => {
  if (!tags?.length) {
    return <Typography sx={{ color: '#697887', fontSize: 13 }}>—</Typography>;
  }

  const visible = tags.slice(0, 2);
  const extra = tags.length - visible.length;

  return (
    <Stack direction="row" flexWrap="wrap" spacing={0.75} useFlexGap>
      {visible.map(tag => (
        <Box
          key={tag}
          component="span"
          sx={{
            alignItems: 'center',
            border: '1px solid #D4DBE6',
            borderRadius: 999,
            color: '#33485A',
            display: 'inline-flex',
            fontSize: 11,
            fontWeight: 600,
            height: 22,
            px: 1,
          }}
        >
          {tag}
        </Box>
      ))}
      {extra > 0 && (
        <Box
          component="span"
          sx={{
            alignItems: 'center',
            border: '1px solid #D4DBE6',
            borderRadius: 999,
            color: '#33485A',
            display: 'inline-flex',
            fontSize: 11,
            fontWeight: 700,
            height: 22,
            px: 0.8,
          }}
        >
          +{extra}
        </Box>
      )}
    </Stack>
  );
};

export const getExplorerFilterPanelSx = (theme: Theme) => {
  const tokens = getThemeTokens(theme);

  return {
    backgroundColor: getSurfaceColor(theme, 'subtle'),
    border: `1px solid ${getSurfaceBorder(theme)}`,
    borderRadius: `${radii.xl}px`,
    px: 2.25,
    py: 2.25,
    '& [class*="BackstageSelect-root"], & [data-catalog-autocomplete-field]': {
      position: 'relative',
    },
    '& [class*="BackstageSelect-formControl"]': {
      display: 'flex',
      flexDirection: 'column',
      margin: 0,
      width: '100%',
    },
    '& [class*="BackstageSelect-formLabel"]': {
      color: `${tokens.textPrimary} !important`,
      fontSize: 14,
      fontWeight: 700,
      lineHeight: 1.2,
      marginBottom: theme.spacing(1),
      position: 'static',
      transform: 'none',
    },
    '& [class*="BackstageAutocomplete-label"]': {
      color: `${tokens.textPrimary} !important`,
      display: 'flex',
      flexDirection: 'column',
      fontSize: 14,
      fontWeight: 700,
      gap: theme.spacing(1),
      margin: 0,
      width: '100%',
    },
    '& [class*="BackstageAutocomplete-label"] > span:first-of-type': {
      color: `${tokens.textPrimary} !important`,
      fontSize: 14,
      fontWeight: 700,
      lineHeight: 1.2,
      position: 'static',
    },
    '& [class*="MuiSelect-select"], & [role="button"]': {
      fontSize: 14,
    },
    '& [class*="BackstageSelectInputBase-root"], & [class*="BackstageAutocompleteBase-inputRoot"]':
      {
        backgroundColor: getSurfaceColor(theme, 'raised'),
        border: `1px solid ${getSurfaceBorder(theme)}`,
        borderRadius: `${radii.md}px`,
        boxShadow: 'none',
        marginTop: '0 !important',
        minHeight: 44,
      },
    '& [class*="BackstageSelectInputBase-root"] .MuiSelect-select': {
      alignItems: 'center',
      display: 'flex',
      minHeight: '36px !important',
      paddingBottom: '0 !important',
      paddingTop: '0 !important',
    },
    '& [class*="BackstageAutocompleteBase-inputRoot"]': {
      alignItems: 'flex-start',
      display: 'flex',
      flexWrap: 'wrap',
      paddingBottom: theme.spacing(0.75),
      paddingLeft: theme.spacing(1.25),
      paddingRight: theme.spacing(4.5),
      paddingTop: theme.spacing(0.75),
    },
    '& [class*="BackstageAutocompleteBase-inputRoot"] fieldset': {
      borderColor: `${getSurfaceBorder(theme)} !important`,
    },
    '& [data-catalog-autocomplete-field] .MuiAutocomplete-tag': {
      margin: theme.spacing(0.25, 0.5, 0.25, 0),
      maxWidth: 'calc(100% - 8px)',
    },
    '& [data-catalog-autocomplete-field] .MuiChip-root': {
      backgroundColor: '#F7FAFC',
      border: '1px solid #D4DDE5',
      borderRadius: 999,
      height: 28,
    },
    '& [data-catalog-autocomplete-field] .MuiChip-label': {
      color: tokens.textPrimary,
      fontSize: 13,
      fontWeight: 600,
      paddingLeft: 12,
      paddingRight: 8,
    },
    '& [data-catalog-autocomplete-field] .MuiChip-deleteIcon': {
      color: '#7A919C',
      fontSize: 18,
      marginLeft: 0,
      marginRight: 6,
    },
    '& [class*="BackstageAutocompleteBase-input"]': {
      fontSize: 14,
      minWidth: 48,
      padding: '4px 0 0 !important',
    },
    '& [class*="BackstageAutocompleteBase-popupIndicator"]': {
      color: `${tokens.textSecondary} !important`,
      marginRight: theme.spacing(0.75),
    },
    '& [data-catalog-autocomplete-field] .MuiAutocomplete-endAdornment': {
      right: 10,
      top: 'calc(50% - 12px)',
    },
    '& [data-catalog-autocomplete-field][data-has-value="false"]::after': {
      color: alpha(tokens.textMuted, 0.9),
      content: 'attr(data-placeholder)',
      fontSize: 14,
      left: 16,
      pointerEvents: 'none',
      position: 'absolute',
      top: 'calc(50% + 8px)',
      transform: 'translateY(-50%)',
    },
    '& [data-catalog-autocomplete-field][data-has-value="false"]:focus-within::after':
      {
        content: 'none',
      },
    '& [data-catalog-autocomplete-field] .MuiAutocomplete-input::placeholder': {
      color: 'transparent',
    },
    '& .MuiCheckbox-root': {
      color: tokens.textMuted,
    },
    '& .MuiCheckbox-root.Mui-checked': {
      color: theme.palette.primary.main,
    },
  };
};

export const getExplorerTableSx = (
  theme: Theme,
  options?: {
    linkColumnIndices?: number[];
  },
) => {
  const tokens = getThemeTokens(theme);
  const linkColumns = options?.linkColumnIndices ?? [];
  const linkSelector = linkColumns
    .map(index => `& tbody tr td:nth-of-type(${index}) a`)
    .join(', ');

  return {
    minWidth: 0,
    '& [class*="BackstageTable-root"]': {
      width: '100%',
    },
    '& .MuiPaper-root': {
      borderRadius: `${radii.xl}px`,
    },
    '& .MuiToolbar-root': {
      minHeight: 'unset',
    },
    '& [class*="MuiToolbar-root"]:first-of-type': {
      alignItems: 'center',
      gap: theme.spacing(2),
      padding: theme.spacing(1.5, 2),
    },
    '& [class*="MuiToolbar-root"]:first-of-type .MuiTypography-h6': {
      color: tokens.textPrimary,
      fontSize: '1.15rem',
      fontWeight: 500,
      lineHeight: 1.1,
    },
    '& [class*="MuiToolbar-root"]:first-of-type .MuiInputBase-root': {
      minWidth: 250,
    },
    '& [class*="MuiToolbar-root"]:first-of-type .MuiInput-underline:before': {
      borderBottomColor: getSurfaceBorder(theme, 'strong'),
    },
    '& [class*="MuiToolbar-root"]:first-of-type .MuiInput-underline:hover:not(.Mui-disabled):before':
      {
        borderBottomColor: getSurfaceBorder(theme, 'strong'),
      },
    '& [class*="MuiToolbar-root"]:first-of-type .MuiInput-underline:after': {
      borderBottomColor: theme.palette.primary.main,
    },
    '& [class*="MuiToolbar-root"]:first-of-type .MuiSvgIcon-root': {
      color: tokens.textPrimary,
    },
    '& thead th': {
      color: `${tokens.textPrimary} !important`,
      fontSize: 13,
      fontWeight: 700,
      letterSpacing: 'normal',
      textTransform: 'none',
    },
    '& tbody td': {
      color: tokens.textPrimary,
      fontSize: 13,
      paddingBottom: theme.spacing(1.35),
      paddingTop: theme.spacing(1.35),
      verticalAlign: 'middle',
    },
    ...(linkSelector
      ? {
          [linkSelector]: {
            color: `${theme.palette.primary.main} !important`,
            fontWeight: 700,
            textDecoration: 'none',
          },
        }
      : {}),
    '& tbody tr td:first-of-type': {
      fontWeight: 700,
    },
    '& .MuiTablePagination-root': {
      borderTop: `1px solid ${getSurfaceBorder(theme)}`,
    },
    '& .MuiIconButton-root, & tbody td svg': {
      color: tokens.textSecondary,
    },
    '& [class*="BackstageFavoriteToggleIcon-root"] svg': {
      fontSize: 18,
    },
  };
};
